import { forwardRef } from 'react';
import { css, cx } from '@linaria/atomic';
import type { CommonHTMLAttributes } from '@frontend/types';
import { theme } from '@frontend/theme';
import { usePortal } from '../../hooks/use-portal';
import type { HintProps } from './use-hint';

type HintComponentProps = HintProps & {
  children: React.ReactNode;
  hasInteractiveContent?: boolean;
};

export type HintUIProps = Omit<HintComponentProps, 'ref'> & CommonHTMLAttributes;

const hintBaseStyle = css`
  background: white;
  border-radius: ${theme.borderRadius.small};
  font-family: ${theme.font.family};
  font-size: ${theme.font.size.large};
  line-height: 1.3;
  margin: 0;
  opacity: 0;
  padding: ${theme.spacing(1)};
  pointer-events: none;
  position: absolute;
  transition: opacity 250ms ease-in-out;

  &:focus {
    outline: none;
  }
`;

const hintVisibleStyle = css`
  opacity: 1;
`;

const hintInteractiveStyle = css`
  pointer-events: all;
`;

export const Hint = forwardRef<HTMLParagraphElement, HintUIProps>(
  ({ buffer, children, hasInteractiveContent, id, placement, className, ...rest }, ref) => {
    const { Portal } = usePortal({
      attributes: { id, style: 'width: 0; height: 0' },
    });
    const visible = !rest['aria-hidden'];

    return (
      <Portal>
        <p
          className={cx(
            hintBaseStyle,
            visible && hintVisibleStyle,
            hasInteractiveContent && visible && hintInteractiveStyle,
            className
          )}
          {...rest}
          ref={ref}
        >
          {children}
        </p>
      </Portal>
    );
  }
);
Hint.displayName = 'Hint';
